import type { JSX, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ShellActions, ShellAppChip, ShellLogo } from './components';
import { useCurrentApp } from './hooks';

export interface PlatformShellUser {
  email?: string | null;
  username?: string | null;
  is_admin?: boolean;
}

export interface PlatformShellProps {
  /** Desktop (full wordmark, more padding) or mobile (mark-only, tighter). */
  variant: 'desktop' | 'mobile';
  /** Optional page title slotted between the app chip and the right actions. */
  title?: string;
  /** The app's main content. Rendered below the shell by each wrapper. */
  children?: ReactNode;
  /** Current authenticated user (for avatar initial and admin checks). */
  user?: PlatformShellUser | null;
  /** Called when the user signs out from the settings menu. */
  onLogout?: () => void | Promise<void>;
  /** Optional unread notifications count (shows the red dot). */
  notificationsCount?: number;
}

/**
 * Canonical platform shell. Both desktop `PlatformShell` and mobile
 * `MobileShell` render this and let CSS tokens re-skin per app.
 *
 * Visual contract: `tmp/platform-mockups/platform-shell.html`.
 * Layout classes live in `shared/platform/theme/tokens.css` under `.shell`.
 *
 * The shell itself is a `<header>`. Wrappers are responsible for the
 * surrounding document flow (flex column, scroll containers, toasters,
 * route-protection, etc).
 */
export function PlatformShell({
  variant,
  title,
  children,
  user,
  onLogout,
  notificationsCount,
}: PlatformShellProps): JSX.Element {
  const appId = useCurrentApp();
  const { pathname } = useLocation();
  const isInsideApp = appId !== 'nerdy-paradise';
  const showAllApps = isInsideApp;
  const isLauncher = pathname === '/' || pathname === '/apps';

  const avatarInitial =
    (user?.email ?? user?.username ?? '?').trim().charAt(0) || '?';

  return (
    <>
      <header
        className="shell"
        data-variant={variant}
        data-app={appId}
      >
        <ShellLogo variant={variant} />

        {!isLauncher && <div className="shell__divider" aria-hidden="true" />}

        <ShellAppChip />

        {title && variant === 'desktop' && (
          <span className="shell__title">{title}</span>
        )}

        <div className="shell__spacer" />

        {showAllApps && variant === 'desktop' && (
          <Link to="/" className="shell__all-apps" title="Back to app launcher">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
            <span>All apps</span>
          </Link>
        )}

        <ShellActions
          variant={variant}
          avatarInitial={avatarInitial}
          isAdmin={Boolean(user?.is_admin)}
          onLogout={onLogout}
          notificationsCount={notificationsCount}
        />
      </header>

      {children}
    </>
  );
}

export default PlatformShell;
